import { Card, Col, DatePicker, Divider, Form, Input, Row } from 'antd'

import {
  Gender,
  ResidenceEnvironment,
  HivAnamnesisFormSection
} from 'src/api/requests'
import { CountrySelect, Text } from 'src/components'
import { PersonDemographicsFields } from 'src/features/persons/components/PersonDemographics'
import { dayMonthYearFormatWithDot, disabledFutureDate } from 'src/libs'
import { useLocales } from 'src/locales'

import { useHivAnamnesisForm } from '../../context/HivAnamnesisFormContext'
import { FieldLabel } from '../FieldLabel'
import { EnumRadio } from '../fields/EnumRadio'
import { HivCuatmSelect } from '../fields/HivCuatmSelect'
import { HivFormItem } from '../HivFormItem'
import { ReadOnlyControl, useIsReadOnlyForm } from '../ReadOnly'
import { SystemRefreshButton } from '../SystemRefreshButton'

const DEMOGRAPHICS_NUMBERS: Record<string, string> = {
  patronymic: '6',
  maritalStatus: '11',
  vitalStatus: '12',
  leftCountry: '13',
  deathDate: '12',
  deathCause: '12.1',
  leftCountryDate: '13',
  leftCountryType: '13',
  arrivedFromAbroadDate: '14',
  arrivedFromCountry: '14'
}

const HOME_COUNTRY = 'md'

/** B. Patient data (fields 5–14). Names and IDNP are read-only for users without access to personal data. */
export const PatientCard = () => {
  const { t } = useLocales()
  const isReadOnly = useIsReadOnlyForm()
  const form = Form.useFormInstance()
  const { userHasAccessToPersonalData } = useHivAnamnesisForm()

  const country = Form.useWatch(['form', 'patient', 'address', 'country'], form)

  // Since 2026-10-15 every field is optional (only 43 is required); a given IDNP keeps its format. Without
  // access to personal data the IDNP and names are hidden by the API and kept from the person record on save.
  const path = (...parts: string[]) => ['form', 'patient', ...parts]

  return (
    <Card
      title={t('hiv_anamnesis.patient.title')}
      extra={
        <SystemRefreshButton sections={[HivAnamnesisFormSection.PATIENT]} />
      }
    >
      <Row gutter={[24, 24]}>
        <Col xs={24} lg={8}>
          <HivFormItem
            name={path('idnp')}
            number="5"
            label={t('hiv_anamnesis.patient.idnp')}
            rules={[
              {
                pattern: /^(2[0-9]{12}|09[0-9]{11})$/,
                message: t('hiv_anamnesis.patient.idnp_format')
              }
            ]}
          >
            <Input
              maxLength={13}
              disabled={!userHasAccessToPersonalData || undefined}
              placeholder={t('hiv_anamnesis.patient.idnp')}
            />
          </HivFormItem>
        </Col>

        <Col xs={24} lg={8}>
          <HivFormItem
            name={path('lastName')}
            number="6"
            label={t('hiv_anamnesis.patient.last_name')}
          >
            <Input
              maxLength={250}
              disabled={!userHasAccessToPersonalData || undefined}
              placeholder={t('hiv_anamnesis.patient.last_name')}
            />
          </HivFormItem>
        </Col>

        <Col xs={24} lg={8}>
          <HivFormItem
            name={path('firstName')}
            number="6"
            label={t('hiv_anamnesis.patient.first_name')}
          >
            <Input
              maxLength={250}
              disabled={!userHasAccessToPersonalData || undefined}
              placeholder={t('hiv_anamnesis.patient.first_name')}
            />
          </HivFormItem>
        </Col>

        <Col xs={24} lg={8}>
          <HivFormItem
            name={path('birthDate')}
            number="7"
            label={t('hiv_anamnesis.patient.birth_date')}
          >
            <DatePicker
              className="full-width"
              format={dayMonthYearFormatWithDot}
              disabledDate={disabledFutureDate}
              placeholder={t('hiv_anamnesis.patient.birth_date')}
            />
          </HivFormItem>
        </Col>

        <Col xs={24} lg={16}>
          <HivFormItem
            name={path('gender')}
            number="8"
            label={t('hiv_anamnesis.patient.gender')}
          >
            <EnumRadio
              values={Object.values(Gender)}
              labelPrefix="hiv_anamnesis.patient.genders"
            />
          </HivFormItem>
        </Col>
      </Row>

      <Divider />
      <Text size="xl">{t('hiv_anamnesis.patient.address')}</Text>

      <Row gutter={[24, 24]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={8}>
          <HivFormItem
            name={path('address', 'country')}
            number="9"
            label={t('hiv_anamnesis.patient.country')}
          >
            <CountrySelect
              allowClear
              placeholder={t('hiv_anamnesis.patient.country')}
              onChange={(value) => {
                if (value !== HOME_COUNTRY) {
                  form.setFieldValue(path('address', 'cuatmLocalityId'), null)
                }
              }}
            />
          </HivFormItem>
        </Col>

        {country === HOME_COUNTRY && (
          <Col xs={24} lg={16}>
            <HivFormItem
              name={path('address', 'cuatmLocalityId')}
              number="9"
              label={t('hiv_anamnesis.patient.locality')}
            >
              <HivCuatmSelect
                placeholder={t('hiv_anamnesis.patient.locality')}
              />
            </HivFormItem>
          </Col>
        )}

        <Col xs={24} lg={8}>
          <HivFormItem
            name={path('address', 'street')}
            label={t('hiv_anamnesis.patient.street')}
          >
            <Input
              maxLength={250}
              placeholder={t('hiv_anamnesis.patient.street')}
            />
          </HivFormItem>
        </Col>

        <Col xs={12} lg={4}>
          <HivFormItem
            name={path('address', 'streetNumber')}
            label={t('hiv_anamnesis.patient.street_number')}
          >
            <Input maxLength={50} />
          </HivFormItem>
        </Col>

        <Col xs={12} lg={4}>
          <HivFormItem
            name={path('address', 'apartmentNumber')}
            label={t('hiv_anamnesis.patient.apartment_number')}
          >
            <Input maxLength={50} />
          </HivFormItem>
        </Col>

        <Col xs={24} lg={8}>
          <HivFormItem
            name={path('phone')}
            number="9"
            label={t('hiv_anamnesis.patient.phone')}
          >
            <Input
              maxLength={50}
              placeholder={t('hiv_anamnesis.patient.phone')}
            />
          </HivFormItem>
        </Col>

        <Col xs={24} lg={8}>
          <HivFormItem
            name={path('address', 'residenceEnvironment')}
            number="10"
            label={t('hiv_anamnesis.patient.residence_environment')}
          >
            <EnumRadio
              values={Object.values(ResidenceEnvironment)}
              labelPrefix="hiv_anamnesis.patient.residence"
            />
          </HivFormItem>
        </Col>
      </Row>

      <Divider />
      <Text size="xl">{t('demographics.title')}</Text>

      <div style={{ marginTop: 16 }}>
        <PersonDemographicsFields
          namePrefix={path('demographics')}
          patronymicDisabled={!userHasAccessToPersonalData}
          renderLabel={(field, label) => (
            <FieldLabel
              number={DEMOGRAPHICS_NUMBERS[field]}
              label={label}
              field={`patient.demographics.${field}`}
            />
          )}
          wrapControl={(control) =>
            isReadOnly ? <ReadOnlyControl>{control}</ReadOnlyControl> : control
          }
        />
      </div>
    </Card>
  )
}
